/**
 * useSearchCorpus — loads and caches the full search corpus for a district.
 *
 * Single cache entry per district, shared by all search consumers on the page:
 *   Phase 0: Dexie cold-start hydration (~5 ms, instant paint after first ever load)
 *   Phase 1: background network refresh (silent SWR — no loading spinner on revisit)
 *   Memory:  same-session cache; second modal-open costs zero I/O
 *
 * All 7 providers load in parallel via Promise.allSettled — partial failures
 * silently degrade to [] for that provider, never blocking the rest.
 *
 * Cache key: ["search-corpus", districtId]
 * Dexie key: `search_corpus_${districtId}`
 */

import { useQueryCore } from "@/hooks/useQueryCore";
// Side-effect: registers all providers into the registry
import { registry } from "../providers";

async function loadAllProviders(districtId) {
  const providers = registry.getAll();
  const results = await Promise.allSettled(
    providers.map((p) => p.load(districtId))
  );
  const corpus = {};
  providers.forEach((p, i) => {
    corpus[p.id] = results[i].status === "fulfilled" ? results[i].value || [] : [];
  });
  return corpus;
}

export function useSearchCorpus(districtId) {
  const { data, loading, refetch } = useQueryCore({
    key: ["search-corpus", districtId],
    enabled: !!districtId,
    queryFn: () => loadAllProviders(districtId),
    cacheKey: districtId ? `search_corpus_${districtId}` : null,
  });

  // useQueryCore initializes data as [] — corpus is a plain object once resolved
  const rawCorpus = data && !Array.isArray(data) ? data : null;

  return { rawCorpus, loading, refetch };
}
